import {
  ActionIcon,
  Box,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { observer } from "mobx-react-lite";
import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { StoreContext } from "../stores/rootStore";
import LatencyDisplay from "./LatencyDisplay";
import ElvGraph from "./ElvGraph";
import React from "react";
import { IconRefresh } from "@tabler/icons-react";
import { GraphData } from "../utils/graph";
import { action } from "mobx";
import { useNavigate } from "react-router-dom";
import RealtimeTimeSelect from "./RealtimeTimeSelect";
import { nHoursAgo, nMinutesAgo } from "../utils/time";
import CountedValueCard from "./CountedValueCard";
import ElvLatencyGraph from "./ElvLatencyGraph";
import Top10Card from "./Top10Card";
import { Top10, Top10Row } from "../apiResponses/sessionsOverviewResponse";
import { notifications } from "@mantine/notifications";

export const RealTimeDisplayHeader: React.FC = observer(() => {
  const rs = useContext(StoreContext);
  const rts = rs as any;

  return (
    <Group mb={25} mt={15} justify="space-between">
      <Group justify="flex-end">
        <ActionIcon
          variant="filled"
          onClick={() => {
            rts.refreshAll();
          }}
        >
          <IconRefresh stroke={2} />
        </ActionIcon>
        <RealtimeTimeSelect store={rts} />
      </Group>
    </Group>
  );
});

export const RealTimeDisplayFooter: React.FC = observer(() => {
  const rs = useContext(StoreContext);
  const rts = rs as any;

  if (rts.contentId === undefined) {
    return <Box h={10} w={10} />;
  }

  const clientsMap = rts.activeClientsReq.getData();
  const contentClients = rts.activeClients;
  const title =
    rts.contentTitle(rts.contentId) === rts.contentId
      ? "-"
      : rts.contentTitle(rts.contentId);

  return (
    <Group mb={25} mt={20} justify="space-between">
      {contentClients === undefined ||
      contentClients.length < 1 ||
      clientsMap === null ? (
        <Box h={10} w={10} />
      ) : (
        <Card shadow="sm" padding="md" radius="md" withBorder w="100%">
          <Text ta="center" fw={700}>
            Active Clients (US only)
          </Text>
          <Text size="xl" ta="center">
            {title}
          </Text>
          <Text ta="center">{rts.contentId}</Text>
          {rts.activeNodes !== null ? (
            <Text ta="center" size="sm">
              Retrieved info from {clientsMap.size} of {rts.activeNodes.length}{" "}
              nodes
            </Text>
          ) : null}
          <Table mt={20} striped withRowBorders highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Client IP</Table.Th>
                <Table.Th>User ID</Table.Th>
                <Table.Th>Email</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contentClients.map((c: any) => {
                return (
                  <Table.Tr>
                    <Table.Td>{c.client_ip}</Table.Td>
                    <Table.Td>{c.user_id}</Table.Td>
                    <Table.Td>{c.email}</Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Group>
  );
});

enum RealtimeMode {
  Sessions = "Sessions",
  Latency = "Latency",
}

export const RealTimeDisplayBody: React.FC = observer(() => {
  const rs = useContext(StoreContext);
  const [mode, setMode] = useState<RealtimeMode>(RealtimeMode.Sessions);
  const navigate = useNavigate();
  const rts = rs as any;
  const latency = rts.latencyReq.getData();
  var data: GraphData | null;
  const onColumnClick = (s: string) => {
    s !== "Others" && rts.contentId === undefined && rts.setContentId(s);
  };

  if (rts.contentId === undefined) {
    data = rts.streamOverviewReq.getData();
    if (data !== null && data.length >= 2) {
      data = [
        data[0]!.map((v, i) => {
          // Skip the time, because that's not a content ID
          if (i === 0) {
            return v;
          }
          if (rts.contentTitle(v as string) !== v) {
            return { value: v as string, label: rts.contentTitle(v as string) };
          } else {
            return v;
          }
        }),
        ...(data?.slice(1) as any),
      ];
    }
  } else {
    data = rts.streamDetailReq.getData();
  }

  const graphOptions = {
    timeRange: [nMinutesAgo(rts.minutes), new Date()] as [Date, Date],
  };

  var graph: ReactNode;
  if (mode === RealtimeMode.Sessions) {
    graph = (
      <ElvGraph
        data={data}
        height={450}
        onColumnClick={onColumnClick}
        options={graphOptions}
        key={`elv-realtimegraph-${rts.contentId ?? "overview"}`}
      />
    );
  } else if (mode === RealtimeMode.Latency) {
    const latencyData = rts.fullLatencyReq.getData();
    graph = <ElvLatencyGraph data={latencyData} height={450} />;
  }

  const handleKeyPress = useCallback((event: any) => {
    if (event.shiftKey && event.ctrlKey && event.key === "L") {
      navigate("/latencyHist");
    } else if (event.shiftKey && event.ctrlKey && event.key === "T") {
      navigate("/graphTest");
    } else if (event.shiftKey && event.ctrlKey && event.key === "E") {
      const objectToCopy = {
        graphOptions: graphOptions,
        graphData: data,
      };
      const text = JSON.stringify(objectToCopy);
      const blob = new Blob([text], { type: "application/json" });
      const downloadLink = document.createElement("a");
      document.body.appendChild(downloadLink);
      downloadLink.href = URL.createObjectURL(blob);
      if (rts.minutes % 60 === 0) {
        downloadLink.download = `graph_testcase_${rts.minutes / 60}_hours.json`;
      } else {
        downloadLink.download = `graph_testcase_${rts.minutes}_minutes.json`;
      }
      downloadLink.click();
      document.body.removeChild(downloadLink);
      notifications.show({
        title: "Downloaded testcase!",
        message: "The graph data has been downloaded.",
        color: "green",
        autoClose: 5000,
      });
    }
  }, []);

  // It's a little sloppy to attach this to the whole document, but whatever
  useEffect(() => {
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <Stack justify="center" align="center" mt={-13}>
      <SimpleGrid cols={2} w="100%">
        {peakSessionsCard(
          rts,
          () => setMode(RealtimeMode.Sessions),
          mode === RealtimeMode.Sessions,
        )}
        {latencyCard(
          rts,
          () => setMode(RealtimeMode.Latency),
          mode === RealtimeMode.Latency,
        )}
      </SimpleGrid>
      {graph}
      <LatencyDisplay data={latency} />
      <Top10Card
        onChange={() => {}}
        onTitleSelect={(v: Top10Row) =>
          action(() => {
            if (rts.contentId === v.id) {
              rts.contentId = undefined;
            } else {
              rts.contentId = v.id;
            }
          })()
        }
        countType={"peak_viewers"}
        selectedTitle={
          rts.contentId ? rts.contentTitle(rts.contentId) : undefined
        }
        top10={top10FromStore(rts)}
      />
    </Stack>
  );
});

function top10FromStore(store: any): Top10 | null {
  const peakPerContent = store.peakSessionsPerContent;
  if (peakPerContent === undefined) {
    return null;
  }

  peakPerContent.sort((a: any, b: any) => b.peak - a.peak);

  return {
    count_type: "peak_viewers",
    rows: peakPerContent
      .map((v: any) => {
        return {
          title: store.contentTitle(v.contentId),
          id: v.contentId,
          count: v.peak,
        };
      })
      .slice(0, 10),
  };
}

function peakSessionsCard(
  rts: any,
  onClick: () => void,
  selected: boolean,
): ReactNode {
  let mainDisplay: string | undefined = undefined;
  let subtext: string | undefined = undefined;
  if (rts.peakSessions !== undefined) {
    if (rts.peakSessions.peak === 0) {
      mainDisplay = `None`;
      if (rts.minutes % 60 === 0) {
        subtext = `in last ${rts.minutes / 60} hours`;
      } else {
        subtext = `in last ${rts.minutes} minutes`;
      }
    } else {
      mainDisplay = rts.peakSessions.peak.toLocaleString();
      subtext = `at ${rts.peakSessions?.time.toLocaleTimeString()}`;
    }
  }
  return (
    <CountedValueCard
      title={"Peak Sessions"}
      value={mainDisplay}
      subtext={subtext}
      onClick={onClick}
      selected={selected}
      key="PeakSessionsCard"
    />
  );
}

function latencyCard(
  rts: any,
  onClick: () => void,
  selected: boolean,
): ReactNode {
  let mainDisplay: string | undefined = undefined;
  let subtext: string | undefined = undefined;
  if (rts.latency !== undefined) {
    mainDisplay = `${rts.latency.average}ms`;
    subtext = `p90 ${rts.latency.p90}ms`;
  } else if (rts.noLatencyData) {
    mainDisplay = `No data`;
    if (rts.minutes % 60 === 0) {
      subtext = `in last ${rts.minutes / 60} hours`;
    } else {
      subtext = `in last ${rts.minutes} minutes`;
    }
  }
  return (
    <CountedValueCard
      title={"Latency"}
      value={mainDisplay}
      subtext={subtext}
      onClick={onClick}
      selected={selected}
      key="LatencyCard"
    />
  );
}
